
import { Component, Vue } from "vue-property-decorator";
import { searchCustomer } from "@/api/customer";
import { COMPANY_OPTIONS } from "@/constant";
import {
  getHqPackingListsPackages,
  searchCustomerHqPackingList,
  queryOriginalPackingListInfoByCLPackingList,
} from "@/api/hq-packing-list";
import { Message } from "element-ui";
import {
  checkPacking,
  createOrUpdateHenganLoadingPlan,
  detailQueryQtyStepInfoByPackingList,
  detailQueryQtyStepInfoByPi,
  downloadPreviewDetail,
  getHenganLoadingPlanProductAssigned,
} from "@/api/hengan-loading-plan";
import { IUserModel } from "@/api/types";
import { getUsersByJobPosition } from "@/api/users";
import { HqPackingListModel } from "@/model/HenganLoadingPlanModels";
import AsyncTooltip from "@/components/AsyncTooltip/index.vue";
import CustomsDeclarationPreviewDialog, {
  CustomsDeclarationPreviewItem,
} from "@/views/sales-management/components/CustomsDeclarationPreviewDialog.vue";
import PackingListProductListDialog, {
  PackingListProductItem,
} from "@/views/components/PackingListProductListDialog.vue";
import PiProductListDialog, {
  PiProductItem,
} from "@/views/components/PiProductListDialog.vue";
import { previewCustomsDeclaration } from "@/api/booking-order";
import { formatPrice, hasMoreThanTwoDecimals } from "@/utils/number";
import HenganLoadingPlanPackingBlock from "./HenganLoadingPlanPackingBlock.vue";
import {
  CascaderOptionItem,
  CreateHenganLoadingPlanRequest,
  Customer,
  CustomsPackingTableRowData,
  HenganLoadingPlanPackingInfo,
  isOverUnderageBlocked,
  PackingInfo,
  PackingListGroup,
  PackingRow,
  sortPackingRowsByCode,
} from "./henganLoadingPlanFormModels";

export interface HenganLoadingPlanEditRow {
  id: string;
  companyCode: string;
  transshipDeclaration: boolean | null;
}

@Component({
  name: "HenganLoadingPlanFormDialog",
  components: {
    AsyncTooltip,
    HenganLoadingPlanPackingBlock,
    CustomsDeclarationPreviewDialog,
    PackingListProductListDialog,
    PiProductListDialog,
  },
})
export default class HenganLoadingPlanFormDialog extends Vue {
  private visible = false;
  private mode: "create" | "update" = "create";
  private pageLoading = false;
  private activePanel: "unadded" | "added" = "unadded";
  private isLoadingCustomers = false;
  private customers: Customer[] = [];
  private selectedHqPackingListIds: string[] = [];
  private companyOptions = COMPANY_OPTIONS;
  private cascaderProps = {
    multiple: true,
    value: "id",
    label: "code",
    emitPath: false,
    disabled: "disabled",
  };
  private cascaderOptions: CascaderOptionItem[] = [];
  private customsPackingTableData: CustomsPackingTableRowData[] = [];
  private rawCustomsPackingTableData: CustomsPackingTableRowData[] = [];
  private createHenganLoadingPlanRequest: CreateHenganLoadingPlanRequest =
    this.getCreateHenganLoadingPlanRequest();
  private logisticsSpecialist: IUserModel[] = [];
  private data: PackingInfo[] = [];
  private packingInfoRequest: HenganLoadingPlanPackingInfo =
    new HenganLoadingPlanPackingInfo();
  private isSubmitting = false;
  private doAddHenganLoadingPlanLoading = false;
  public logisticsName = "";
  private showStockNot = false;
  private searchCustomsPackingLoading = false;
  private cascaderLoading = false;
  private downloadPreviewDetailLoading = false;
  private selectionTick = 0;
  private blockExpandedMap: Record<string, Record<string, boolean>> = {
    unadded: {},
    added: {},
  };
  private unaddedSearch = {
    hqPackingListCode: "",
    factoryName: "",
  };
  private addedSearch = {
    hqPackingListCode: "",
    factoryName: "",
  };
  private remarkDialogVisible = false;
  private remarkDraft = "";
  private previewVisible = false;
  private previewLoading = false;
  private previewItems: CustomsDeclarationPreviewItem[] = [];
  private packingListProductVisible = false;
  private packingListProductLoading = false;
  private packingListProductItems: PackingListProductItem[] = [];
  private piProductVisible = false;
  private piProductLoading = false;
  private piProductItems: PiProductItem[] = [];

  get isCreate() {
    return this.mode === "create";
  }

  get remarkText() {
    return (this.createHenganLoadingPlanRequest.remark || "").trim();
  }

  get remarkPreview() {
    const text = this.remarkText;
    if (text.length <= 18) {
      return text;
    }
    return text.slice(0, 18) + "…";
  }

  get assignedHqPackingListIds(): string[] {
    return Array.from(
      new Set(
        this.data
          .map((item) => item.hqPackingListId)
          .filter((id) => !!id)
      )
    );
  }

  get unaddedGroups(): PackingListGroup[] {
    return this.buildGroupsFromUnadded(this.customsPackingTableData);
  }

  get addedGroups(): PackingListGroup[] {
    return this.buildGroupsFromAdded(this.data);
  }

  get filteredUnaddedGroups(): PackingListGroup[] {
    return this.filterGroups(this.unaddedGroups, this.unaddedSearch);
  }

  get filteredAddedGroups(): PackingListGroup[] {
    return this.filterGroups(this.addedGroups, this.addedSearch);
  }

  get unaddedItemCount() {
    return this.customsPackingTableData.length;
  }

  get addedItemCount() {
    return this.data.length;
  }

  get hasUnaddedSelection() {
    return this.customsPackingTableData.some((row) => row.selected);
  }

  get hasAddedSelection() {
    return this.data.some((row) => row.selected);
  }

  get unaddedSelectedVolume() {
    return this.customsPackingTableData
      .filter((row) => row.selected)
      .reduce((sum, row) => sum + Number(row.volume || 0), 0)
      .toFixed(2);
  }

  get unaddedSelectedPrice() {
    const total = this.customsPackingTableData
      .filter((row) => row.selected)
      .reduce((sum, row) => sum + Number(row.totalPrice || 0), 0);
    return formatPrice(total);
  }

  get addedTotalVolume() {
    return this.data
      .reduce((sum, row) => sum + Number(row.volume || 0), 0)
      .toFixed(2);
  }

  get addedTotalPrice() {
    const total = this.data.reduce(
      (sum, row) => sum + Number(row.totalPrice || 0),
      0
    );
    return formatPrice(total);
  }

  get emptyUnaddedText() {
    if (!this.selectedHqPackingListIds.length) {
      return "先选择进仓装箱单，再点击「加载包装」";
    }
    return "当前进仓装箱单下暂无未添加主包装";
  }

  public openCreate() {
    this.resetState();
    this.mode = "create";
    this.getLogisticsSpecialist();
    this.visible = true;
  }

  public async openEdit(rowData: HenganLoadingPlanEditRow) {
    this.resetState();
    this.mode = "update";
    this.visible = true;
    this.pageLoading = true;
    try {
      const data_: any = await getHenganLoadingPlanProductAssigned(rowData.id);
      this.packingInfoRequest = data_;
      this.data = (this.packingInfoRequest.packingInfos || []).map((item) => {
        this.$set(item, "selected", false);
        // 已添加接口不返回超欠发；已加入出库计划的主包装按 NONE 处理
        this.$set(item, "overUnderageStatus", "NONE");
        return item;
      });
      this.createHenganLoadingPlanRequest.customerId =
        this.packingInfoRequest.customerId;
      this.createHenganLoadingPlanRequest.logisticsSpecialistId =
        this.packingInfoRequest.logisticsSpecialistId;
      this.createHenganLoadingPlanRequest.companyCode = rowData.companyCode;
      this.createHenganLoadingPlanRequest.transshipDelivery =
        !!this.packingInfoRequest.transshipDelivery;
      this.createHenganLoadingPlanRequest.remark =
        this.packingInfoRequest.remark || "";
      this.createHenganLoadingPlanRequest.transshipDeclaration =
        rowData.transshipDeclaration !== null
          ? !rowData.transshipDeclaration
          : rowData.transshipDeclaration;
      this.logisticsName =
        this.packingInfoRequest.logisticsSpecialistName || "";

      await this.searchHqPackingList(true);
      this.selectedHqPackingListIds = [...this.assignedHqPackingListIds];
      this.applyCascaderLocks();
      if (this.selectedHqPackingListIds.length) {
        await this.searchCustomsPacking();
      }
      this.activePanel = "added";
    } catch (error) {
      console.error(error);
      Message.error({
        message: (error as any)?.message || "加载出库计划失败",
        duration: 2000,
      });
    } finally {
      this.pageLoading = false;
    }
  }

  private resetState() {
    this.activePanel = "unadded";
    this.selectedHqPackingListIds = [];
    this.customsPackingTableData = [];
    this.rawCustomsPackingTableData = [];
    this.createHenganLoadingPlanRequest = this.getCreateHenganLoadingPlanRequest();
    this.data = [];
    this.packingInfoRequest = new HenganLoadingPlanPackingInfo();
    this.logisticsName = "";
    this.customers = [];
    this.cascaderOptions = [];
    this.cascaderLoadedKey = "";
    this.showStockNot = false;
    this.unaddedSearch = { hqPackingListCode: "", factoryName: "" };
    this.addedSearch = { hqPackingListCode: "", factoryName: "" };
    this.blockExpandedMap = { unadded: {}, added: {} };
    this.selectionTick = 0;
    this.remarkDialogVisible = false;
    this.remarkDraft = "";
  }

  private searchCustomers(keyword) {
    this.isLoadingCustomers = true;
    searchCustomer({ keyword })
      .then((res: any) => {
        this.customers = res;
        this.isLoadingCustomers = false;
      })
      .catch(() => {
        this.isLoadingCustomers = false;
      });
  }

  private cascaderLoadedKey = "";

  private getCascaderLoadKey() {
    const customerId = this.createHenganLoadingPlanRequest.customerId || "";
    const companyCode = this.createHenganLoadingPlanRequest.companyCode || "";
    return `${customerId}|${companyCode}`;
  }

  private async searchHqPackingList(silent = false) {
    if (!this.createHenganLoadingPlanRequest.customerId) {
      if (!silent) {
        Message.error({ message: "请选择客户", duration: 2000 });
      }
      return;
    }
    if (!this.createHenganLoadingPlanRequest.companyCode) {
      if (!silent) {
        Message.error({ message: "请选择公司", duration: 2000 });
      }
      return;
    }
    const loadKey = this.getCascaderLoadKey();
    if (
      loadKey === this.cascaderLoadedKey &&
      this.cascaderOptions.length > 0 &&
      this.cascaderOptions[0].id !== "loading"
    ) {
      this.applyCascaderLocks();
      return;
    }
    this.cascaderOptions = [
      {
        id: "loading",
        code: "加载中...",
        disabled: true,
        bePackaged: false,
        factoryAbbreviation: "",
        children: [],
      },
    ];
    this.cascaderLoading = true;
    try {
      const res: any = await searchCustomerHqPackingList(
        this.createHenganLoadingPlanRequest.customerId,
        { companyCode: this.createHenganLoadingPlanRequest.companyCode }
      );
      this.cascaderOptions = res || [];
      this.cascaderLoadedKey = loadKey;
      this.applyCascaderLocks();
    } catch (error) {
      this.cascaderLoadedKey = "";
      this.cascaderOptions = [];
      if (!silent) {
        Message.error({
          message: (error as any)?.message || "加载近仓装箱单失败",
          duration: 2000,
        });
      }
    } finally {
      this.cascaderLoading = false;
    }
  }

  private async searchCustomsPacking() {
    if (!this.selectedHqPackingListIds.length) {
      Message.error({ message: "请先选择进仓装箱单", duration: 2000 });
      return;
    }
    this.searchCustomsPackingLoading = true;
    try {
      const res: any = await getHqPackingListsPackages({
        ids: this.selectedHqPackingListIds.join(","),
      });
      const hqPackingLists: HqPackingListModel[] = res.map(
        (value) => value.hqPackingList
      );
      const customsPackingTableData = hqPackingLists
        .map((hqPackingList) =>
          hqPackingList.customsPackingSet.map(
            (value) =>
              new CustomsPackingTableRowData(
                hqPackingList.deliveryNoteId,
                hqPackingList.id,
                hqPackingList.deliveryNoteCode,
                hqPackingList.code,
                hqPackingList.state,
                hqPackingList.factoryName,
                value
              )
          )
        )
        .reduce((pre, cur) => pre.concat(cur), []);
      this.rawCustomsPackingTableData = customsPackingTableData;
      this.refreshUnaddedList();
      this.ensureBlockExpanded("unadded", this.unaddedGroups.map((g) => g.id));
    } catch (error) {
      this.$message.error((error as any).message);
    } finally {
      this.searchCustomsPackingLoading = false;
    }
  }

  private refreshUnaddedList() {
    const assignedIds = new Set(
      this.data.filter((v) => v.packingType === "C").map((c) => c.packingId)
    );
    let list = this.rawCustomsPackingTableData.filter(
      (c) => !assignedIds.has(c.id)
    );
    if (!this.showStockNot) {
      list = list.filter(
        (c) => c.inventory !== "YES" && c.inventory !== "MIX"
      );
    }
    list.forEach((row) => {
      row.selected = false;
    });
    this.customsPackingTableData = list;
    this.selectionTick += 1;
  }

  private onShowStockChange() {
    if (this.rawCustomsPackingTableData.length) {
      this.refreshUnaddedList();
    }
  }

  private onCascaderChange(val: string[]) {
    const locked = this.assignedHqPackingListIds;
    const next = Array.from(new Set([...(val || []), ...locked]));
    const removedLocked = locked.some((id) => !(val || []).includes(id));
    if (removedLocked) {
      Message.warning({
        message: "已添加入出库计划进仓装箱单不可取消勾选",
        duration: 2500,
      });
    }
    this.selectedHqPackingListIds = next;
    this.applyCascaderLocks();
  }

  private applyCascaderLocks() {
    const locked = new Set(this.assignedHqPackingListIds);
    const walk = (nodes: CascaderOptionItem[]) => {
      (nodes || []).forEach((node: CascaderOptionItem & { _apiDisabled?: boolean }) => {
        const children = node.children || [];
        if (children.length) {
          walk(children);
          return;
        }
        if (node.id === "loading") {
          return;
        }
        if (node._apiDisabled === undefined) {
          this.$set(node, "_apiDisabled", !!node.disabled);
        }
        this.$set(
          node,
          "disabled",
          !!(node._apiDisabled || locked.has(node.id))
        );
      });
    };
    walk(this.cascaderOptions);
  }

  private async doAddHenganLoadingPlan() {
    const selectedRows = this.customsPackingTableData.filter(
      (row) => row.selected
    );
    if (!selectedRows.length) {
      Message.error({ message: "请先勾选要添加的主包装", duration: 2000 });
      return;
    }
    this.doAddHenganLoadingPlanLoading = true;
    try {
      const packingInfos = selectedRows.map((customsRow) => {
        const info = new PackingInfo(
          customsRow.id,
          "C",
          0,
          !!customsRow.loadingFirst,
          customsRow.displayCode,
          customsRow.declarationDescriptions,
          customsRow.volume,
          customsRow.purchaseOrderNumbers,
          customsRow.factoryName,
          customsRow.totalProductQuantity,
          customsRow.totalPrice,
          customsRow.hqPackingListCode,
          customsRow.deliveryNoteCode,
          customsRow.hqPackingListId,
          customsRow.deliveryNoteId,
          false
        );
        info.netWeight = customsRow.netWeight;
        info.grossWeight = customsRow.grossWeight;
        info.inventory = customsRow.inventory;
        info.useInventoryQuantity = customsRow.useInventoryQuantity;
        info.descriptions = customsRow.descriptions;
        info.purchaseOrderExceptionsProcessed =
          customsRow.purchaseOrderExceptionsProcessed;
        info.overUnderageStatus = "NONE";
        info.exception = customsRow.exception;
        info.hqPackingListState = customsRow.hqPackingListState;
        return info;
      });
      await checkPacking(
        packingInfos
          .filter((item) => item?.packingId != null)
          .map((item) => item.packingId)
      );
      this.data.push(...packingInfos);
      this.createHenganLoadingPlanRequest.packingInfos = this.data;
      this.refreshUnaddedList();
      this.applyCascaderLocks();
      this.ensureBlockExpanded(
        "added",
        this.addedGroups.map((g) => g.id)
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.doAddHenganLoadingPlanLoading = false;
    }
  }

  private removeSelectedPackingInfos() {
    const selected = this.data.filter((item) => item.selected);
    if (!selected.length) {
      Message.error({ message: "请先勾选要移除的主包装", duration: 2000 });
      return;
    }
    const ids = new Set(selected.map((item) => item.packingId));
    this.rehydrateUnaddedPoolFromPackingInfos(selected);
    this.data = this.data.filter((item) => !ids.has(item.packingId));
    this.createHenganLoadingPlanRequest.packingInfos = this.data;
    this.refreshUnaddedList();
    this.applyCascaderLocks();
    this.ensureBlockExpanded(
      "unadded",
      this.unaddedGroups.map((g) => g.id)
    );
    this.selectionTick += 1;
  }

  /**
   * 编辑时加载包装接口不含本计划已占用主包装。
   * 移除已添加项时，把它们回灌进 raw，才能重新出现在「未添加」。
   */
  private rehydrateUnaddedPoolFromPackingInfos(packingInfos: PackingInfo[]) {
    const existingIds = new Set(
      this.rawCustomsPackingTableData.map((row) => row.id)
    );
    packingInfos.forEach((info) => {
      if (!info?.packingId || existingIds.has(info.packingId)) {
        return;
      }
      const row = CustomsPackingTableRowData.fromPackingInfo(info);
      this.rawCustomsPackingTableData.push(row);
      existingIds.add(info.packingId);
    });
  }

  private async downloadPreviewDetail() {
    if (!this.data.length) {
      Message.error({
        message: "请先添加主包装后再预览",
        duration: 2000,
      });
      return;
    }
    const ids = this.data.map((item) => item.packingId);
    this.downloadPreviewDetailLoading = true;
    try {
      await downloadPreviewDetail(ids);
    } catch (err) {
      console.error(err);
    } finally {
      this.downloadPreviewDetailLoading = false;
    }
  }

  private closeDialog() {
    this.visible = false;
    this.resetState();
  }

  public async doCreateOrUpdateHenganLoadingPlan() {
    this.isSubmitting = true;
    this.createHenganLoadingPlanRequest.packingInfos = this.data;
    const payload: CreateHenganLoadingPlanRequest = {
      ...this.createHenganLoadingPlanRequest,
      transshipDeclaration:
        !this.createHenganLoadingPlanRequest.transshipDeclaration,
    };
    try {
      await createOrUpdateHenganLoadingPlan(payload, "0");
      this.visible = false;
      this.resetState();
      this.$notify({
        title: "Success",
        message: "创建成功",
        type: "success",
        duration: 2000,
      });
      this.$emit("success");
    } catch (err) {
      console.error(err);
    } finally {
      this.isSubmitting = false;
    }
  }

  public async doUpdateHenganLoadingPlan() {
    this.isSubmitting = true;
    this.createHenganLoadingPlanRequest.packingInfos = this.data;
    try {
      const data: any = await createOrUpdateHenganLoadingPlan(
        this.createHenganLoadingPlanRequest,
        this.packingInfoRequest.henganLoadingPlanId
      );
      if (data && data.message) {
        Message.error({ message: data.message, duration: 2000 });
      }
      this.visible = false;
      this.resetState();
      this.$notify({
        title: "Success",
        message: "更新成功",
        type: "success",
        duration: 2000,
      });
      this.$emit("success");
    } catch (err) {
      console.error(err);
    } finally {
      this.isSubmitting = false;
    }
  }

  private async getLogisticsSpecialist() {
    if (!this.logisticsSpecialist || this.logisticsSpecialist.length < 1) {
      const data: any = await getUsersByJobPosition("logistics-clerk");
      this.logisticsSpecialist = data;
    }
  }

  private bindLogistics() {
    if (this.createHenganLoadingPlanRequest.customerId) {
      this.customers.forEach((value) => {
        if (value.id === this.createHenganLoadingPlanRequest.customerId) {
          this.logisticsName = value.logisticsSpecialistName;
          this.createHenganLoadingPlanRequest.logisticsSpecialistId =
            value.logisticsSpecialistId;
        }
      });
    }
    this.customsPackingTableData = [];
    this.rawCustomsPackingTableData = [];
    this.selectedHqPackingListIds = [];
    this.data = [];
    this.cascaderOptions = [];
    this.cascaderLoadedKey = "";
    if (
      this.createHenganLoadingPlanRequest.customerId &&
      this.createHenganLoadingPlanRequest.companyCode
    ) {
      this.searchHqPackingList(true);
    }
  }

  private getCreateHenganLoadingPlanRequest(): CreateHenganLoadingPlanRequest {
    const req = new CreateHenganLoadingPlanRequest();
    req.transshipDelivery = false;
    req.transshipDeclaration = false;
    req.remark = "";
    req.packingInfos = [];
    return req;
  }

  private async fetchNodeDetails(node: any, data: any) {
    if (!node.isLeaf) return "";
    try {
      const res: any = await queryOriginalPackingListInfoByCLPackingList(
        data.id
      );
      if (res && res.length > 0) {
        return res;
      }
      return "";
    } catch (e) {
      console.error(e);
      return "";
    }
  }

  private openRemarkDialog() {
    this.remarkDraft = this.createHenganLoadingPlanRequest.remark || "";
    this.remarkDialogVisible = true;
  }

  private confirmRemark() {
    this.createHenganLoadingPlanRequest.remark = this.remarkDraft || "";
    this.remarkDialogVisible = false;
  }

  private onSelectionChange() {
    this.selectionTick += 1;
  }

  private isBlockExpanded(section: string, id: string) {
    const map = this.blockExpandedMap[section] || {};
    return map[id] !== false;
  }

  private setBlockExpanded(section: string, id: string, val: boolean) {
    if (!this.blockExpandedMap[section]) {
      this.$set(this.blockExpandedMap, section, {});
    }
    this.$set(this.blockExpandedMap[section], id, val);
  }

  private ensureBlockExpanded(section: string, ids: string[]) {
    ids.forEach((id) => {
      if (this.blockExpandedMap[section][id] === undefined) {
        this.$set(this.blockExpandedMap[section], id, true);
      }
    });
  }

  private isSectionAllExpanded(section: "unadded" | "added") {
    const groups =
      section === "unadded"
        ? this.filteredUnaddedGroups
        : this.filteredAddedGroups;
    if (!groups.length) {
      return true;
    }
    return groups.every((group) => this.isBlockExpanded(section, group.id));
  }

  private toggleSectionExpanded(section: "unadded" | "added") {
    const groups =
      section === "unadded"
        ? this.filteredUnaddedGroups
        : this.filteredAddedGroups;
    const expand = !this.isSectionAllExpanded(section);
    groups.forEach((group) => {
      this.setBlockExpanded(section, group.id, expand);
    });
  }

  private canSelectPacking(section: "unadded" | "added", row: PackingRow) {
    if (section === "added") {
      return true;
    }
    return !(
      row.inventory === "MIX" ||
      row.inventory === "YES" ||
      isOverUnderageBlocked(row.overUnderageStatus) ||
      row.exception
    );
  }

  private getSectionSelectableRows(
    section: "unadded" | "added"
  ): PackingRow[] {
    const groups =
      section === "unadded"
        ? this.filteredUnaddedGroups
        : this.filteredAddedGroups;
    return groups
      .reduce((list, group) => list.concat(group.items || []), [] as PackingRow[])
      .filter((row) => this.canSelectPacking(section, row));
  }

  private hasSectionSelectable(section: "unadded" | "added") {
    return this.getSectionSelectableRows(section).length > 0;
  }

  private isSectionAllSelected(section: "unadded" | "added") {
    const rows = this.getSectionSelectableRows(section);
    return rows.length > 0 && rows.every((row) => !!row.selected);
  }

  private toggleSectionSelection(section: "unadded" | "added") {
    const rows = this.getSectionSelectableRows(section);
    if (!rows.length) {
      return;
    }
    const checked = !this.isSectionAllSelected(section);
    rows.forEach((row) => {
      this.$set(row, "selected", checked);
    });
    this.selectionTick += 1;
  }

  private buildGroupsFromUnadded(
    rows: CustomsPackingTableRowData[]
  ): PackingListGroup[] {
    const map = new Map<string, PackingListGroup>();
    rows.forEach((row) => {
      const id = row.hqPackingListId || "unknown";
      if (!map.has(id)) {
        map.set(id, {
          id,
          code: row.hqPackingListCode || "-",
          factoryName: row.factoryName || "-",
          deliveryNoteCode: row.deliveryNoteCode || "",
          items: [],
        });
      }
      map.get(id).items.push(row);
    });
    return Array.from(map.values()).map((group) => ({
      ...group,
      items: sortPackingRowsByCode(group.items),
    }));
  }

  private buildGroupsFromAdded(rows: PackingInfo[]): PackingListGroup[] {
    const map = new Map<string, PackingListGroup>();
    rows.forEach((row) => {
      const id = row.hqPackingListId || "unknown";
      if (!map.has(id)) {
        map.set(id, {
          id,
          code: row.hqPackingListCode || "-",
          factoryName: row.factoryName || "-",
          deliveryNoteCode: row.deliveryNoteCode || "",
          items: [],
        });
      }
      map.get(id).items.push(row);
    });
    return Array.from(map.values()).map((group) => ({
      ...group,
      items: sortPackingRowsByCode(group.items),
    }));
  }

  private filterGroups(
    groups: PackingListGroup[],
    search: { hqPackingListCode: string; factoryName: string }
  ) {
    const code = (search.hqPackingListCode || "").trim().toLowerCase();
    const factory = (search.factoryName || "").trim().toLowerCase();
    return groups.filter((group) => {
      const codeOk =
        !code || (group.code || "").toLowerCase().includes(code);
      const factoryOk =
        !factory || (group.factoryName || "").toLowerCase().includes(factory);
      return codeOk && factoryOk;
    });
  }

  private async declarationPreview() {
    const customsPackingIds = this.data.map((item) => item.packingId);
    const data = {
      customerId: this.createHenganLoadingPlanRequest.customerId,
      customsPackingIds,
    };
    this.previewItems = [];
    this.previewVisible = true;
    this.previewLoading = true;
    try {
      this.previewItems = (await previewCustomsDeclaration(data)) as any;
    } finally {
      this.previewLoading = false;
    }
  }

  private async showPackingListProductList() {
    this.packingListProductItems = [];
    this.packingListProductVisible = true;
    this.packingListProductLoading = true;
    try {
      const cIds = this.data.map((item) => item.packingId);
      this.packingListProductItems = (await detailQueryQtyStepInfoByPackingList({
        packingIds: cIds,
      })) as any;
    } finally {
      this.packingListProductLoading = false;
    }
  }

  private async showPiProductList() {
    this.piProductItems = [];
    this.piProductVisible = true;
    this.piProductLoading = true;
    try {
      const cIds = this.data.map((item) => item.packingId);
      this.piProductItems = (await detailQueryQtyStepInfoByPi({
        packingIds: cIds,
      })) as any;
    } finally {
      this.piProductLoading = false;
    }
  }

  private hasMoreThanTwoDecimals = hasMoreThanTwoDecimals;
}
